import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import type {
  TaskListDefinition,
  TaskDropdownDefinition,
  TemplateTaskConfig,
  TaskStatus,
  SavedChecklist,
  SavedAudit,
} from '@techgitter/contracts';
import type { AdditionalDetailsValues, TemplateCustomerContext } from '../types/ticketTypeTemplate';
import type { LiveChecklist, LiveSection, LiveTask, ManualTaskInput, ChecklistProgress } from '../types/taskList';
import {
  resolveChecklist,
  resolveStandaloneTaskList,
  reEvaluateVisibility,
  computeChecklistProgress,
  snapshotChecklist,
  buildAuditEntry,
  buildSavedAudit,
} from '../utils/checklistEngine';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseChecklistOptions {
  additionalDetails: AdditionalDetailsValues;
  customer: TemplateCustomerContext;
  userName: string;
  /** Reusable dropdown option sets, used to resolve dropdown-task options. */
  taskDropdowns?: TaskDropdownDefinition[];
  /**
   * Full task-list catalog, needed to expand nested TaskListReferences when a
   * standalone list is attached via `attachTaskList`. Without it, references
   * silently resolve to "missing" and their sections are dropped.
   */
  taskListCatalog?: TaskListDefinition[];
}

export interface UseChecklistReturn {
  checklist: LiveChecklist | null;
  auditEntries: string[];
  hasBlockingTasks: boolean;
  progress: ChecklistProgress | null;
  warnings: string[];

  // Mutations
  attachTaskList: (
    taskListDef: TaskListDefinition,
    overrides?: { additionalDetails?: AdditionalDetailsValues; customer?: TemplateCustomerContext },
  ) => void;
  applyTemplateConfig: (
    config: TemplateTaskConfig,
    catalog: TaskListDefinition[],
    overrides?: { additionalDetails?: AdditionalDetailsValues; customer?: TemplateCustomerContext },
  ) => void;
  detachAll: () => void;
  setTaskStatus: (sectionId: string, taskId: string, status: TaskStatus) => void;
  setTaskNote: (sectionId: string, taskId: string, note: string) => void;
  setTaskOption: (sectionId: string, taskId: string, option: string) => void;
  removeTask: (sectionId: string, taskId: string) => void;
  restoreTask: (sectionId: string, taskId: string) => void;
  removeSection: (sectionId: string) => void;
  restoreSection: (sectionId: string) => void;
  addManualTask: (input: ManualTaskInput) => void;

  getSnapshotForSave: () => { checklist: SavedChecklist; audit: SavedAudit } | null;
}

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

export function useChecklist(options: UseChecklistOptions): UseChecklistReturn {
  const { additionalDetails, customer, userName, taskDropdowns, taskListCatalog } = options;
  const dropdowns = useMemo(() => taskDropdowns ?? [], [taskDropdowns]);
  const catalog = useMemo(() => taskListCatalog ?? [], [taskListCatalog]);

  const [checklist, setChecklist] = useState<LiveChecklist | null>(null);
  const [auditEntries, setAuditEntries] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);

  // Track attachment count per taskListId for composite ID generation
  const attachmentCountsRef = useRef<Map<string, number>>(new Map());

  // ---------------------------------------------------------------------------
  // Re-evaluate visibility when field values change
  // ---------------------------------------------------------------------------

  // Signature of the outcome-relevant task state (status, selected option,
  // whether a note is present). `taskOutcome` conditions depend on these, so
  // visibility must re-evaluate when they change — not only on field/customer
  // changes. Visibility flips don't alter this signature, so the effect below
  // converges without looping.
  const outcomeSignature = useMemo(() => {
    if (!checklist) return '';
    return checklist.sections
      .flatMap((s) =>
        s.tasks.map(
          (t) => `${t.taskId}:${t.status}:${t.selectedOption ?? ''}:${t.note.trim() ? 1 : 0}`,
        ),
      )
      .join('|');
  }, [checklist]);

  useEffect(() => {
    if (!checklist) return;
    const updated = reEvaluateVisibility(checklist, additionalDetails, customer);
    if (updated !== checklist) {
      setChecklist(updated);
    }
  }, [additionalDetails, customer, outcomeSignature]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep dropdown-task options in sync with the catalog. The catalog can
  // finish loading *after* the checklist was first resolved (its fetch is
  // async), so re-resolve each dropdown task's options by id whenever it
  // changes. Only `options` is touched — status/notes are preserved.
  useEffect(() => {
    const dropdownMap = new Map(dropdowns.map(d => [d.dropdownId, d.options]));
    setChecklist(prev => {
      if (!prev) return prev;
      let changed = false;
      const sections = prev.sections.map(section => {
        let tasksChanged = false;
        const tasks = section.tasks.map(task => {
          if (!task.dropdownId) return task;
          const options = dropdownMap.get(task.dropdownId) ?? [];
          const same =
            task.options != null &&
            task.options.length === options.length &&
            task.options.every((o, i) => o === options[i]);
          if (same) return task;
          tasksChanged = true;
          return { ...task, options };
        });
        if (!tasksChanged) return section;
        changed = true;
        return { ...section, tasks };
      });
      return changed ? { sections } : prev;
    });
  }, [dropdowns]);

  // ---------------------------------------------------------------------------
  // Progress computation
  // ---------------------------------------------------------------------------

  const progress = useMemo<ChecklistProgress | null>(() => {
    if (!checklist) return null;
    return computeChecklistProgress(checklist);
  }, [checklist]);

  const hasBlockingTasks = progress?.hasBlockingTasks ?? false;

  // ---------------------------------------------------------------------------
  // Audit helper
  // ---------------------------------------------------------------------------

  const addAudit = useCallback((entry: string) => {
    setAuditEntries(prev => [...prev, entry]);
  }, []);

  // ---------------------------------------------------------------------------
  // Section/task mutation helpers
  // ---------------------------------------------------------------------------

  const updateSection = useCallback((
    sectionId: string,
    updater: (section: LiveSection) => LiveSection,
  ) => {
    setChecklist(prev => {
      if (!prev) return prev;
      return {
        sections: prev.sections.map(s => s.sectionId === sectionId ? updater(s) : s),
      };
    });
  }, []);

  const updateTask = useCallback((
    sectionId: string,
    taskId: string,
    updater: (task: LiveTask) => LiveTask,
  ) => {
    updateSection(sectionId, section => ({
      ...section,
      tasks: section.tasks.map(t => t.taskId === taskId ? updater(t) : t),
    }));
  }, [updateSection]);

  // ---------------------------------------------------------------------------
  // Public mutations
  // ---------------------------------------------------------------------------

  const attachTaskList = useCallback((
    taskListDef: TaskListDefinition,
    overrides?: { additionalDetails?: AdditionalDetailsValues; customer?: TemplateCustomerContext },
  ) => {
    const counts = attachmentCountsRef.current;
    const currentCount = counts.get(taskListDef.taskListId) ?? 0;
    counts.set(taskListDef.taskListId, currentCount + 1);

    const effectiveDetails = overrides?.additionalDetails
      ? { ...additionalDetails, ...overrides.additionalDetails }
      : additionalDetails;
    const effectiveCustomer = overrides?.customer
      ? { ...customer, ...overrides.customer }
      : customer;

    const newSections = resolveStandaloneTaskList(
      taskListDef,
      currentCount,
      effectiveDetails,
      effectiveCustomer,
      catalog,
      dropdowns,
    );

    setChecklist(prev => {
      const existing = prev?.sections ?? [];
      return { sections: [...existing, ...newSections] };
    });
  }, [additionalDetails, customer, dropdowns, catalog]);

  const applyTemplateConfig = useCallback((
    config: TemplateTaskConfig,
    catalog: TaskListDefinition[],
    overrides?: { additionalDetails?: AdditionalDetailsValues; customer?: TemplateCustomerContext },
  ) => {
    const effectiveDetails = overrides?.additionalDetails
      ? { ...additionalDetails, ...overrides.additionalDetails }
      : additionalDetails;
    const effectiveCustomer = overrides?.customer
      ? { ...customer, ...overrides.customer }
      : customer;
    const result = resolveChecklist(config, catalog, effectiveDetails, effectiveCustomer, dropdowns);
    setWarnings(result.warnings);

    setChecklist(prev => {
      const existing = prev?.sections ?? [];
      // Merge: existing manually attached sections come first, then template sections
      return { sections: [...existing, ...result.checklist.sections] };
    });

    // Reset attachment counts for template-driven lists
    attachmentCountsRef.current = new Map();
  }, [additionalDetails, customer, dropdowns]);

  const detachAll = useCallback(() => {
    setChecklist(null);
    setAuditEntries([]);
    setWarnings([]);
    attachmentCountsRef.current = new Map();
  }, []);

  const setTaskStatus = useCallback((sectionId: string, taskId: string, status: TaskStatus) => {
    setChecklist(prev => {
      if (!prev) return prev;
      const section = prev.sections.find(s => s.sectionId === sectionId);
      const task = section?.tasks.find(t => t.taskId === taskId);
      if (!task || task.status === status) return prev;

      addAudit(buildAuditEntry('status-change', {
        taskTitle: task.title,
        from: task.status,
        to: status,
      }, userName));

      return {
        sections: prev.sections.map(s =>
          s.sectionId === sectionId
            ? {
                ...s,
                tasks: s.tasks.map(t =>
                  t.taskId === taskId ? { ...t, status } : t
                ),
              }
            : s
        ),
      };
    });
  }, [userName, addAudit]);

  const setTaskNote = useCallback((sectionId: string, taskId: string, note: string) => {
    updateTask(sectionId, taskId, task => ({ ...task, note }));
    // Notes are not audited per plan
  }, [updateTask]);

  // Dropdown-task selection: choosing an option marks the task done;
  // clearing it (empty string) reverts to not-done. Audited only when
  // the done/not-done state actually flips.
  const setTaskOption = useCallback((sectionId: string, taskId: string, option: string) => {
    setChecklist(prev => {
      if (!prev) return prev;
      const section = prev.sections.find(s => s.sectionId === sectionId);
      const task = section?.tasks.find(t => t.taskId === taskId);
      if (!task) return prev;
      const selectedOption = option || undefined;
      const nextStatus: TaskStatus = selectedOption ? 'completed' : 'new';
      if (task.selectedOption === selectedOption && task.status === nextStatus) return prev;

      if (task.status !== nextStatus) {
        addAudit(buildAuditEntry('status-change', {
          taskTitle: task.title,
          from: task.status,
          to: nextStatus,
        }, userName));
      }

      return {
        sections: prev.sections.map(s =>
          s.sectionId === sectionId
            ? {
                ...s,
                tasks: s.tasks.map(t =>
                  t.taskId === taskId
                    ? { ...t, selectedOption, status: nextStatus }
                    : t
                ),
              }
            : s
        ),
      };
    });
  }, [userName, addAudit]);

  const removeTask = useCallback((sectionId: string, taskId: string) => {
    setChecklist(prev => {
      if (!prev) return prev;
      const section = prev.sections.find(s => s.sectionId === sectionId);
      const task = section?.tasks.find(t => t.taskId === taskId);
      if (!task) return prev;

      addAudit(buildAuditEntry('task-remove', { taskTitle: task.title }, userName));

      return {
        sections: prev.sections.map(s =>
          s.sectionId === sectionId
            ? {
                ...s,
                tasks: s.tasks.map(t =>
                  t.taskId === taskId ? { ...t, isRemoved: true } : t
                ),
              }
            : s
        ),
      };
    });
  }, [userName, addAudit]);

  const restoreTask = useCallback((sectionId: string, taskId: string) => {
    setChecklist(prev => {
      if (!prev) return prev;
      const section = prev.sections.find(s => s.sectionId === sectionId);
      const task = section?.tasks.find(t => t.taskId === taskId);
      if (!task) return prev;

      addAudit(buildAuditEntry('task-restore', { taskTitle: task.title }, userName));

      return {
        sections: prev.sections.map(s =>
          s.sectionId === sectionId
            ? {
                ...s,
                tasks: s.tasks.map(t =>
                  t.taskId === taskId ? { ...t, isRemoved: false } : t
                ),
              }
            : s
        ),
      };
    });
  }, [userName, addAudit]);

  const removeSection = useCallback((sectionId: string) => {
    setChecklist(prev => {
      if (!prev) return prev;
      const section = prev.sections.find(s => s.sectionId === sectionId);
      if (!section) return prev;

      addAudit(buildAuditEntry('section-remove', { sectionLabel: section.label }, userName));

      return {
        sections: prev.sections.map(s =>
          s.sectionId === sectionId ? { ...s, isRemoved: true } : s
        ),
      };
    });
  }, [userName, addAudit]);

  const restoreSection = useCallback((sectionId: string) => {
    setChecklist(prev => {
      if (!prev) return prev;
      const section = prev.sections.find(s => s.sectionId === sectionId);
      if (!section) return prev;

      addAudit(buildAuditEntry('section-restore', { sectionLabel: section.label }, userName));

      return {
        sections: prev.sections.map(s =>
          s.sectionId === sectionId ? { ...s, isRemoved: false } : s
        ),
      };
    });
  }, [userName, addAudit]);

  const addManualTask = useCallback((input: ManualTaskInput) => {
    const taskId = `manual-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const newTask: LiveTask = {
      taskId,
      title: input.title,
      description: input.description,
      status: 'new',
      note: '',
      noteRequired: input.noteRequired,
      isRemoved: false,
      isVisible: true,
      source: 'manual',
    };

    const sectionLabel = checklist?.sections.find(s => s.sectionId === input.sectionId)?.label;
    addAudit(buildAuditEntry('task-add', {
      taskTitle: input.title,
      sectionLabel: sectionLabel ?? input.sectionId,
    }, userName));

    updateSection(input.sectionId, section => ({
      ...section,
      tasks: [...section.tasks, newTask],
    }));
  }, [checklist, userName, addAudit, updateSection]);

  // ---------------------------------------------------------------------------
  // Snapshot for save
  // ---------------------------------------------------------------------------

  const getSnapshotForSave = useCallback((): { checklist: SavedChecklist; audit: SavedAudit } | null => {
    if (!checklist || checklist.sections.length === 0) return null;

    return {
      checklist: snapshotChecklist(checklist, new Date().toISOString()),
      audit: buildSavedAudit(auditEntries),
    };
  }, [checklist, auditEntries]);

  return {
    checklist,
    auditEntries,
    hasBlockingTasks,
    progress,
    warnings,
    attachTaskList,
    applyTemplateConfig,
    detachAll,
    setTaskStatus,
    setTaskNote,
    setTaskOption,
    removeTask,
    restoreTask,
    removeSection,
    restoreSection,
    addManualTask,
    getSnapshotForSave,
  };
}
